import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { MessageSquare, Pencil } from "lucide-react";

import { DataTable } from "components/ui/DataTable";
import { Pill, PILL_COLORS } from "components/ui/Pill";
import { pathGenerators } from "utils/routes";

import {
  formatAddress,
  formatDateOfBirth,
  formatGenderText,
  getGenderPillColors,
} from "./utils";

export const PatientsTable = ({
  patients = [],
  isLoading = false,
  searchQuery = "",
  onSearchChange,
  onNameClick,
  onEdit,
  onSendSms,
  totalRecords = 0,
  currentPage = 1,
  onPageChange,
  rowsPerPage,
  onRowsChange,
  emptyTitle = "No patients found",
  emptyText = "You don't have any patients at this time.",
  loadingTitle = "Loading patients...",
  loadingText = "Please wait while we fetch your patients.",
  searchPlaceholder = "Search",
  showSearch = true,
}) => {
  const columns = useMemo(
    () => [
      {
        id: "patientName",
        header: "Patient Name",
        width: "minmax(min-content, 1fr)",
        render: (patient) => (
          <Link
            to={pathGenerators.patient.details(patient.id)}
            style={{
              color: "var(--green-700)",
              textDecoration: "none",
              fontWeight: 700,
              display: "flex",
              alignItems: "center",
              gap: "8px",
              overflow: "hidden",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.textDecoration = "underline")}
            onMouseLeave={(e) => (e.currentTarget.style.textDecoration = "none")}
            onClick={(e) => {
              if (e.button === 1 || e.ctrlKey || e.metaKey) return;
              e.preventDefault();
              onNameClick?.(patient);
            }}
            data-testid="patient-name"
          >
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>
              {patient.firstName} {patient.surname || ""}
            </span>
            {patient.died && (
              <span style={{ flexShrink: 0 }}>
                <Pill {...PILL_COLORS.deceased}>Deceased</Pill>
              </span>
            )}
          </Link>
        ),
        getTooltipText: (patient) =>
          `${patient.firstName} ${patient.surname || ""}`.trim(),
      },
      {
        id: "dateOfBirth",
        header: "Date of Birth",
        width: "150px",
        render: (patient) => formatDateOfBirth(patient.dateOfBirth),
      },
      {
        id: "address",
        header: "Address",
        width: "270px",
        render: (patient) => formatAddress(patient.address),
        getTooltipText: (patient) => formatAddress(patient.address),
      },
      {
        id: "sex",
        header: "Sex",
        width: "110px",
        render: (patient) => (
          <Pill {...getGenderPillColors(patient.gender)}>
            {formatGenderText(patient.gender)}
          </Pill>
        ),
      },
      {
        id: "phone",
        header: "Phone",
        width: "170px",
        render: (patient) => patient.homePhone || patient.mobilePhone || "-",
      },
      {
        id: "doctor",
        header: "Doctor",
        width: "170px",
        render: (patient) =>
          patient.doctorDetails
            ? `${patient.doctorDetails.firstName} ${patient.doctorDetails.lastName}`
            : "Not set",
        getTooltipText: (patient) =>
          patient.doctorDetails
            ? `${patient.doctorDetails.firstName} ${patient.doctorDetails.lastName}`
            : "Not set",
      },
      {
        id: "email",
        header: "Email",
        width: "250px",
        render: (patient) => patient.email || "-",
        getTooltipText: (patient) => patient.email || "-",
      },
    ],
    [onNameClick]
  );

  const rowActions = useMemo(
    () => [
      {
        label: "Send SMS",
        icon: MessageSquare,
        onClick: (patient) => onSendSms?.(patient),
      },
      {
        label: "Edit",
        icon: Pencil,
        onClick: (patient) => onEdit?.(patient),
      },
    ],
    [onSendSms, onEdit]
  );

  return (
    <DataTable
      columns={columns}
      data={patients}
      getRowId={(row) => row.id}
      isLoading={isLoading}
      emptyTitle={emptyTitle}
      emptyText={emptyText}
      loadingTitle={loadingTitle}
      loadingText={loadingText}
      search={
        showSearch && onSearchChange
          ? {
              value: searchQuery,
              onChange: onSearchChange,
              placeholder: searchPlaceholder,
            }
          : undefined
      }
      rowActions={rowActions}
      currentPage={currentPage}
      rowsValue={rowsPerPage}
      onRowsChange={onRowsChange}
      totalCount={totalRecords}
      onPageChange={onPageChange}
    />
  );
};
